import { ChangeEvent, useState } from "react"
import { css, cx } from "@emotion/css"
import { useI18n } from "providers/I18n"

import { queryCache, useMutation, useQuery } from "react-query"
import { fetchMediaTypeChannels } from "api/globalMediaTypes"
import { createBaseRate, createBaseRateChannels } from "api/ratecard"

import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@material-ui/core"

import Button from "ui/buttons/Button"

type AddInitialChannelsProps = {
  rateCardId: string
  supplierId: string
  mediaTypeId: string
}

export function AddInitialChannels(props: AddInitialChannelsProps) {
  const { rateCardId, supplierId, mediaTypeId } = props
  const { t } = useI18n()

  const ps = new URLSearchParams({ supplierId, mediaTypeId })

  const { data: supplierChannels } = useQuery(
    ["supplier-channels", ps.toString()],
    fetchMediaTypeChannels,
    { enabled: Boolean(supplierId) && Boolean(mediaTypeId) },
  )
  const options =
    supplierChannels?.map((x) => ({ value: x.id, label: x.name })) ?? []

  const [channels, setChannels] = useState<string[]>(() => [])
  const handleMultiChange = (event: ChangeEvent<{ value: unknown }>) => {
    setChannels(event.target.value as string[])
  }
  //

  const [createChannels, l1] = useMutation(createBaseRateChannels)
  const [createEmptyBaseRate, l2] = useMutation(createBaseRate)

  const handleDone = () => {
    createChannels({
      rateCardId,
      channels,
    }).then(() => {
      queryCache.invalidateQueries(["rate-card-details", rateCardId])
    })
  }

  const handleSkip = () => {
    createEmptyBaseRate({ rateCardId }).then(() => {
      queryCache.invalidateQueries(["rate-card-details", rateCardId])
    })
  }

  return (
    <div className={styles.container}>
      <p className={styles.title}>{t("rate.card.initial.channels.title")}</p>
      <p className={styles.subtitle}>
        {t("rate.card.initial.channels.subtitle")}
      </p>

      <div className={cx(styles.pb, styles.inputWrapper)}>
        <FormControl fullWidth variant="outlined">
          <InputLabel className={styles.inputLabel}>
            {t("rate.card.initial.channels.label")}
          </InputLabel>
          <Select
            fullWidth
            multiple
            placeholder="Choose Channels"
            value={channels}
            onChange={handleMultiChange}
            MenuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
              PaperProps: {
                style: {
                  marginTop: "var(--spacing-m)",
                },
              },
            }}
            renderValue={(selected) => {
              const vs = (selected as string[]) ?? []
              if (vs.length === 0) return ""
              return vs
                .map((v) => options.find((o) => o.value === v))
                .filter(Boolean)
                .map((x) => x?.label)
                .join(", ")
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox
                  color="primary"
                  checked={channels.includes(option.value)}
                />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {!!channels.length ? (
        <div className={styles.pb}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleDone}
            disabled={l1.isLoading || l2.isLoading}
          >
            {t("actions.done")}
          </Button>
        </div>
      ) : null}
      <div className={styles.pb}>
        <Button
          color="primary"
          onClick={handleSkip}
          disabled={l1.isLoading || l2.isLoading}
        >
          {t("actions.skip")}
        </Button>
      </div>
    </div>
  )
}

const styles = {
  container: css`
    padding: var(--spacing-xxl-5) var(--spacing-m);
    margin: 0 auto;
    width: max-content;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  title: css`
    padding-bottom: var(--spacing-xs);
    font-size: 13px;
    line-height: 16px;
    font-weight: bold;
  `,
  subtitle: css`
    padding-bottom: var(--spacing-xs);
    font-size: 13px;
    line-height: 16px;
    font-weight: 300;
    color: var(--dark-grey);
  `,
  pb: css`
    padding-bottom: var(--spacing-xl);
  `,
  inputWrapper: css`
    padding-top: var(--spacing-m);
    min-width: 256px;
    max-width: 352px;
  `,
  inputLabel: css`
    background-color: #fff;
    padding: 0 4px;
  `,
}
